import { HelpCircleIcon, PhoneIcon, Trash2Icon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { type Control, type FieldPath, useController, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import ConfirmDialog from "../../../../../components/ui/confirmDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "../../../../../components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../../../components/ui/tooltip";
import { Checkbox } from "../../../../../components/ui/uikit/checkbox";
import { CurrencyInput } from "../../../../../components/ui/uikit/currencyInput";
import { DateInput } from "../../../../../components/ui/uikit/dateInput";
import { Input } from "../../../../../components/ui/uikit/input";
import { Textarea } from "../../../../../components/ui/uikit/textarea";
import { useCheckForceExit } from "../../../../../hooks/useCheckForceExit";
import { roundToDecimals } from "../../../../../lib/money";
import { cn } from "../../../../../lib/utils";
import ForceExitWarningModal from "../../../../exit/ForceExitWarningModal";
import { resolveLatestVerificationOutcome, verificationOutcomeColor } from "../api/callScript.mapping";
import { useCallScriptHistory } from "../api/useCallScriptHistory";
import { CallScriptModal } from "../CallScript/CallScriptModal";
import { applyDigitFormatWithCaret } from "../caretPreservingDigitFormat";
import {
  type ApplicantSsnType,
  EMPLOYER_OPTIONAL_INCOME_TYPE_CODES,
  EXTENSION_MASK_PLACEHOLDER,
  getDefaultGrossNet,
  getIncomeTypeOptions,
  GROSS_PAY_OPTIONAL_INCOME_TYPE_CODES,
  INCOME_ORDINAL_LABELS,
  type IncomeTypeOption,
  OCCUPATION_INCOME_TYPE_CODES,
  PHONE_EXTENSION_MAX_LENGTH,
  W2_INCOME_TYPE_CODE,
} from "../constants";
import { computeTaxes } from "../incomeCalculator/incomeCalculator";
import type { PayFrequency } from "../incomeCalculator/incomeCalculator.constants";
import { NET_PAY_FACTOR, PAY_FREQUENCY_FACTOR } from "../incomeCalculator/incomeCalculator.constants";
import type { IncomeBreakdownResult } from "../incomeCalculator/incomeCalculator.types";
import type { RenderableFieldConfig } from "../incomeFields.config";
import { INCOME_FIELDS } from "../incomeFields.config";
import { useFormReadOnly } from "../readOnlyContext";
import { getSurfacedSelect } from "../selectSurfacing";
import type { IncomeInput, SignersInput } from "../Signers.schema";
import { buildTelHref } from "./buildTelHref";
import { IncomeCalculatorPanel } from "./IncomeCalculatorPanel";
import { IncomeTaxesPanel } from "./IncomeTaxesPanel";
import { PhoneNumberInput } from "./PhoneNumberInput";
import { PullPurchasedEmployerInfo } from "./PullPurchasedEmployerInfo";
import { SubsectionHeader } from "./SubsectionHeader";
import { useZipAutofill } from "./useZipAutofill";
import { ZipLookupMessage } from "./ZipLookupMessage";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface IncomeBlockProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
  onRemove?: () => void;
  /** Deal / approval id, threaded down for the Pull Purchased Employer Info flow. */
  approvalId?: string;
  /** Real applicant id for this person, threaded down for the Pull Purchased Employer Info flow. */
  applicantId?: string;
  /** Applicant SSN/ITIN status — filters the Income Type options. */
  applicantSsnType: ApplicantSsnType;
  /** Called after this block's employer-info pull succeeds, with whether its recalc was deferred (OR-23544). */
  onWorknumberRecalcDeferred?: (deferred: boolean) => void;
  /**
   * Called (by the applicant's W2 income block only) once eligibility
   * resolves, with whether recap-service's `lastPullStatus` is `"NOTFOUND"`
   * for this applicant — threaded up so SignerColumn can render a single
   * warning above the Income section.
   */
  onEmployerInfoLastPullNotFound?: (notFound: boolean) => void;
  /** Applicant-level "no purchased employer data ever found" signal, computed from the index-0 block's eligibility — disables the Pull Purchased Employer Info button on every block for this applicant. */
  employerInfoNotFound?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getFieldName(personPath: PersonPath, index: number, fieldName: keyof IncomeInput): FieldPath<SignersInput> {
  return `${personPath}.incomes.${index}.${fieldName}` as FieldPath<SignersInput>;
}

// ---------------------------------------------------------------------------
// SelectField — uses useFormField to wire aria props to SelectTrigger directly
// ---------------------------------------------------------------------------
interface SelectFieldInnerProperties {
  value: string;
  onValueChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  label: string;
  required?: boolean;
  className?: string;
  hidePlaceholder?: boolean;
}

function SelectFieldInner({
  value,
  onValueChange,
  options,
  label,
  required,
  className,
  hidePlaceholder,
}: SelectFieldInnerProperties) {
  const { t } = useTranslation();
  const { formItemId, formDescriptionId, formMessageId, error } = useFormField();
  // Radix Select ignores the enclosing disabled <fieldset>, so honour the lock
  // explicitly from the shared read-only context.
  const readOnly = useFormReadOnly();
  // Use shared surfacing helper: surfaces an out-of-options value and computes
  // the remount key so Radix Select can display it from the first render.
  const { resolvedOptions, selectKey } = getSurfacedSelect(value, options);
  return (
    <Select key={selectKey} value={value} onValueChange={onValueChange} disabled={readOnly}>
      <SelectTrigger
        id={formItemId}
        aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
        aria-invalid={error ? true : undefined}
        aria-label={label}
        className={className}
        disabled={readOnly}
      >
        <SelectValue placeholder={hidePlaceholder ? undefined : t("review.selectPlaceholder", { label })} />
        {required && <span className="sr-only">{t("review.required")}</span>}
      </SelectTrigger>
      <SelectContent>
        {resolvedOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// NumberFieldContent — the numeric-kind field, backed by uikit's currency
// Input. A real component (not inline in a render-prop) so it gets its own
// fiber instead of running inside Controller's render.
// ---------------------------------------------------------------------------
interface NumberFieldContentProperties {
  field: {
    value: unknown;
    // "" (not undefined) on clear — an explicit RHF onChange(undefined) unsets
    // the field and falls back to defaultValues instead of staying empty.
    onChange: (value: number | "") => void;
    onBlur: () => void;
    name: string;
    ref: React.Ref<HTMLInputElement>;
  };
  placeholder?: string;
  className?: string;
  invalid?: boolean;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  // Injected by FormControl's Slot (id wires up FormLabel's htmlFor).
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

function NumberFieldContent({
  field,
  placeholder,
  className,
  invalid,
  disabled,
  onFocus,
  onBlur,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: NumberFieldContentProperties) {
  const rawValue = field.value;
  const value = typeof rawValue === "number" ? rawValue : undefined;

  return (
    <CurrencyInput
      id={id}
      name={field.name}
      disabled={disabled}
      aria-describedby={ariaDescribedBy}
      // `invalid` forces the red border on a failed lookup, independent of
      // FormControl's RHF-driven aria-invalid passed through from Slot.
      aria-invalid={invalid ? true : ariaInvalid}
      placeholder={placeholder}
      className={className}
      value={value}
      onChange={(next) => field.onChange(next ?? "")}
      onFocus={onFocus}
      onBlur={() => {
        field.onBlur();
        onBlur?.();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Individual field renderer
// ---------------------------------------------------------------------------
interface FieldRendererProperties {
  config: RenderableFieldConfig;
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
  /** Number fields only — optional focus handler, runs before any typing. */
  onFocus?: () => void;
  /** Optional blur handler for text/number/date inputs (runs after RHF's onBlur). */
  onBlur?: () => void;
  /** Force the invalid (red-border) state independent of RHF validation — e.g. a failed ZIP lookup. */
  invalid?: boolean;
  /** Overrides a select config's static options (e.g. SSN/ITIN-filtered Income Type). */
  optionsOverride?: ReadonlyArray<{ value: string; label: string }>;
  /** Renders the control non-editable (e.g. Gross Monthly Pay, which is derived-only). */
  disabled?: boolean;
  /** Overrides a config's static `required` (e.g. Employer Name is optional for Fixed Income (Net) / Unemployed). */
  requiredOverride?: boolean;
  /** Number fields only — intercepts an edit before it reaches RHF; call `apply` to let it through. */
  onChangeOverride?: (next: number | "", apply: (value: number | "") => void) => void;
  /** Select fields only — intercepts a pick before it reaches RHF; call `apply` to let it through. */
  onValueChangeOverride?: (next: string, apply: (value: string) => void) => void;
}

function FieldRenderer({
  config,
  control,
  personPath,
  index,
  onFocus,
  onBlur,
  invalid,
  optionsOverride,
  disabled,
  requiredOverride,
  onChangeOverride,
  onValueChangeOverride,
}: FieldRendererProperties) {
  const { t } = useTranslation();
  const fieldPath = getFieldName(personPath, index, config.name);
  const label = t(config.label);
  const isRequired = requiredOverride ?? config.required;

  if (config.kind === "select") {
    return (
      <FormField
        control={control}
        name={fieldPath}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {label}
              {isRequired && (
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              )}
            </FormLabel>
            <SelectFieldInner
              value={field.value == null ? "" : String(field.value)}
              onValueChange={
                onValueChangeOverride ? (next) => onValueChangeOverride(next, field.onChange) : field.onChange
              }
              options={optionsOverride ?? config.options}
              label={label}
              required={isRequired}
              className={config.controlClassName}
              hidePlaceholder={config.hidePlaceholder}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (config.kind === "textarea") {
    return (
      <FormField
        control={control}
        name={fieldPath}
        render={({ field }) => (
          <FormItem className="col-span-full">
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                rows={1}
                className={config.controlClassName}
                value={field.value == null ? "" : String(field.value)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (config.kind === "number") {
    const placeholder = config.placeholder ? t(config.placeholder) : undefined;
    return (
      <FormField
        control={control}
        name={fieldPath}
        render={({ field }) => (
          <FormItem className={config.itemClassName}>
            <FormLabel className={cn(config.hideLabel ? "sr-only" : config.labelClassName)}>
              {label}
              {isRequired && (
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              )}
            </FormLabel>
            <FormControl>
              <NumberFieldContent
                field={
                  onChangeOverride ? { ...field, onChange: (next) => onChangeOverride(next, field.onChange) } : field
                }
                placeholder={placeholder}
                className={config.controlClassName}
                invalid={invalid}
                disabled={disabled}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // text | phone | date
  return (
    <FormField
      control={control}
      name={fieldPath}
      render={({ field }) => (
        <FormItem className={config.itemClassName}>
          <FormLabel className={cn(config.hideLabel ? "sr-only" : config.labelClassName)}>
            {label}
            {isRequired && (
              <span className="text-destructive ml-0.5" aria-hidden="true">
                *
              </span>
            )}
          </FormLabel>
          <FormControl>
            {config.kind === "date" ? (
              <DateInput
                disabled={disabled}
                // Force the red-border invalid state on a failed ZIP lookup. Spread
                // conditionally so other fields keep FormControl's RHF-driven
                // aria-invalid untouched.
                {...(invalid ? { "aria-invalid": true } : {})}
                placeholder={config.placeholder ? t(config.placeholder) : undefined}
                className={config.controlClassName}
                value={field.value == null ? "" : String(field.value)}
                // DateInput reports the ISO string directly; RHF's onChange takes
                // a raw value just as happily as an event.
                onChange={field.onChange}
                onBlur={() => {
                  field.onBlur();
                  onBlur?.();
                }}
              />
            ) : (
              <Input
                {...field}
                type="text"
                inputMode={config.inputMode}
                disabled={disabled}
                {...(invalid ? { "aria-invalid": true } : {})}
                placeholder={config.placeholder ? t(config.placeholder) : undefined}
                className={config.controlClassName}
                value={field.value == null ? "" : String(field.value)}
                maxLength={config.maxLength}
                onChange={
                  config.format
                    ? (event: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(applyDigitFormatWithCaret(event, config.format!))
                    : field.onChange
                }
                onBlur={() => {
                  if (config.kind === "text" && config.uppercase) {
                    const current = field.value == null ? "" : String(field.value);
                    const upper = current.toUpperCase();
                    if (upper !== current) {
                      field.onChange(upper);
                    }
                  }
                  field.onBlur();
                  onBlur?.();
                }}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// PhoneExtensionField — Employment Verification Phone + Extension grouped together.
// The two inputs live in their own row so an error message (rendered outside
// that row) never shifts the extension. Label sits above the inputs below lg,
// and to the left (centered) at lg.
// ---------------------------------------------------------------------------
interface PhoneExtensionFieldProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
  /** Employer Name / Employment Verification Phone are optional for Fixed Income (Net) / Unemployed. */
  required: boolean;
  /** Deal / approval id, threaded down for the Call Script modal. */
  approvalId?: string;
  /** Real applicant id for this person, threaded down for the Call Script modal. */
  applicantId?: string;
}

function PhoneExtensionField({
  control,
  personPath,
  index,
  required,
  approvalId,
  applicantId,
}: PhoneExtensionFieldProperties) {
  const { t } = useTranslation();
  const uid = useId();
  const phonePath = getFieldName(personPath, index, "employerPhone");
  const extensionPath = getFieldName(personPath, index, "phoneExtension");
  const extension = useController({ control, name: extensionPath });
  const verificationPhoneLabel = t("review.fields.verificationPhone");
  // The call icon (<a>) ignores the enclosing disabled <fieldset>, so under the
  // VLA lock it's disabled explicitly from the shared read-only context.
  const readOnly = useFormReadOnly();
  const extensionDigits = (extension.field.value == null ? "" : String(extension.field.value)).replaceAll(/\D/g, "");
  const [callScriptOpen, setCallScriptOpen] = useState(false);
  const [forceExitWarningOpen, setForceExitWarningOpen] = useState(false);
  const { canProceed: canOpenCallScript } = useCheckForceExit();

  const handleCallScriptClick = () => {
    if (canOpenCallScript) {
      setCallScriptOpen(true);
    } else {
      setForceExitWarningOpen(true);
    }
  };

  return (
    <>
      <FormField
        control={control}
        name={phonePath}
        render={({ field, fieldState }) => {
          const numberDigits = (field.value == null ? "" : String(field.value)).replaceAll(/\D/g, "");
          return (
            <FormItem>
              {/* Label on its own line; phone + extension + Call + Call Script on the
              next line at every breakpoint. FormMessage stays a stacked sibling
              below so it never renders inline. */}
              <div className="flex flex-col gap-2">
                <FormLabel className="whitespace-nowrap">
                  {verificationPhoneLabel}
                  {required && (
                    <span className="text-destructive ml-0.5" aria-hidden="true">
                      *
                    </span>
                  )}
                </FormLabel>
                {/* Phone + Extn share one row so neither moves when an error shows */}
                <div className="flex items-start gap-x-1">
                  <FormControl>
                    <PhoneNumberInput
                      idBase={`${uid}-verification-phone`}
                      value={field.value == null ? "" : String(field.value)}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ariaLabel={verificationPhoneLabel}
                      required={required}
                      invalid={Boolean(fieldState.error)}
                    />
                  </FormControl>
                  <Input
                    {...extension.field}
                    type="text"
                    inputMode="numeric"
                    aria-label={t("review.fields.verificationPhoneExt")}
                    placeholder={EXTENSION_MASK_PLACEHOLDER}
                    className="w-20"
                    value={extension.field.value == null ? "" : String(extension.field.value)}
                    onChange={(event) => {
                      extension.field.onChange(
                        event.target.value.replaceAll(/\D/g, "").slice(0, PHONE_EXTENSION_MAX_LENGTH)
                      );
                    }}
                    maxLength={PHONE_EXTENSION_MAX_LENGTH}
                  />
                  {/* Call icon — dials the entered number (plus extension, as a DTMF
                  pause) via tel: when a number is present and the deal is
                  editable. Under the VLA lock (read-only), or when no number is
                  entered yet, the same slot shows a muted, inert icon instead of
                  announcing a dead link to assistive technology. */}
                  {numberDigits && !readOnly ? (
                    <a
                      href={buildTelHref(numberDigits, extensionDigits)}
                      aria-label={t("review.income.call")}
                      className="text-success-foreground border-success-foreground hover:bg-success flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors"
                    >
                      <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
                    </a>
                  ) : (
                    <span
                      aria-hidden="true"
                      className="text-muted-foreground border-muted flex size-6 shrink-0 items-center justify-center rounded-md border opacity-60"
                    >
                      <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
                    </span>
                  )}
                  {readOnly ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex shrink-0">
                          <button
                            type="button"
                            onClick={handleCallScriptClick}
                            disabled
                            aria-disabled
                            className="bg-primary text-primary-foreground border-primary hover:bg-primary/90 disabled:hover:bg-primary flex h-6 shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-3 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {t("review.income.callScript")}
                          </button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right">{t("review.income.callScriptReadOnly")}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCallScriptClick}
                      className="bg-primary text-primary-foreground border-primary hover:bg-primary/90 flex h-6 shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-3 text-xs font-semibold transition-colors"
                    >
                      {t("review.income.callScript")}
                    </button>
                  )}
                </div>
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <CallScriptModal
        open={callScriptOpen}
        onOpenChange={setCallScriptOpen}
        approvalId={approvalId}
        applicantId={applicantId}
        personPath={personPath}
        index={index}
        control={control}
      />
      <ForceExitWarningModal
        open={forceExitWarningOpen}
        onOpenChange={setForceExitWarningOpen}
        onConfirm={() => setForceExitWarningOpen(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// DifferentWorkLocation checkbox — rendered full-width outside the loop
// ---------------------------------------------------------------------------
interface DifferentWorkLocationFieldProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
}

function DifferentWorkLocationField({ control, personPath, index }: DifferentWorkLocationFieldProperties) {
  const { t } = useTranslation();
  const fieldPath = getFieldName(personPath, index, "differentWorkLocation");
  const { field, fieldState } = useController({
    control,
    name: fieldPath,
  });
  const errorId = `${fieldPath}-error`;

  return (
    <div className="col-span-full flex items-center gap-2">
      <Checkbox
        id={fieldPath}
        checked={Boolean(field.value)}
        onCheckedChange={(checked) => {
          field.onChange(checked === true);
        }}
        aria-describedby={fieldState.error ? errorId : undefined}
        aria-invalid={fieldState.error ? true : undefined}
      />
      <label htmlFor={fieldPath} className="cursor-pointer text-xs font-normal">
        {t("review.income.differentWorkLocation")}
      </label>
      {fieldState.error && (
        <p id={errorId} className="text-destructive text-2xs">
          {fieldState.error.message}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// IncomeBlock
// ---------------------------------------------------------------------------
export function IncomeBlock({
  control,
  personPath,
  index,
  onRemove,
  approvalId,
  applicantId,
  applicantSsnType,
  onWorknumberRecalcDeferred,
  onEmployerInfoLastPullNotFound,
  employerInfoNotFound,
}: IncomeBlockProperties) {
  const { t } = useTranslation();
  const { errors } = useFormState({ control, name: `${personPath}.incomes.${index}` as FieldPath<SignersInput> });
  const hasError = Boolean(errors[personPath]?.incomes?.[index]);
  // Income Type options filtered by the applicant's SSN/ITIN status (a non-SSN
  // applicant may only pick Self-Employed / Unemployed / Cash Income).
  const incomeTypeOptions = getIncomeTypeOptions(applicantSsnType);
  // Fixed Income (Net) / Unemployed have no employer, so Employer Name and
  // Employment Verification Phone are optional for those income types (OR-23412).
  const watchedIncomeType = useWatch({
    control,
    name: getFieldName(personPath, index, "employmentType"),
  }) as IncomeInput["employmentType"];
  const isEmployerRequired = !EMPLOYER_OPTIONAL_INCOME_TYPE_CODES.has(watchedIncomeType);
  // Unemployed has no income amount to report, so Gross Monthly Pay is
  // optional for that income type (OR-24934).
  const isGrossPayRequired = !GROSS_PAY_OPTIONAL_INCOME_TYPE_CODES.has(watchedIncomeType);
  // RHF only revalidates the field that just changed (incomeType), so a stale
  // CLIENT-side "required" error on Employer Name / Employment Verification
  // Phone would otherwise linger after switching to/from an employer-optional
  // Income Type until the user blurs them again. Re-trigger validation only
  // when one of them is already showing an error, so switching Income Type
  // never surfaces a new premature error on an untouched field.
  //
  // Excludes a `type: "server"` error (a live backend violation applied by
  // useServerFieldErrors.ts) from this re-trigger: with no resolver, `trigger()`
  // has no rules left to fail, so it would unconditionally clear ANY error it's
  // asked to re-check, including a backend violation that's still true — this
  // mechanism must only ever clean up a genuinely-stale CLIENT-side error.
  const { trigger } = useFormContext<SignersInput>();
  const employerNamePath = getFieldName(personPath, index, "employerName");
  const verificationPhonePath = getFieldName(personPath, index, "employerPhone");
  const { fieldState: employerNameFieldState } = useController({ control, name: employerNamePath });
  const { fieldState: verificationPhoneFieldState } = useController({ control, name: verificationPhonePath });
  useEffect(() => {
    // Only the field(s) that actually have a stale CLIENT error go to
    // trigger() — batching in a field whose error is still `type: "server"`
    // would clear that live backend violation too (trigger() has no rules
    // left to fail without a resolver, so it always "passes" whatever it's
    // asked to re-check).
    const staleClientPaths = [
      employerNameFieldState.error?.type !== "server" && employerNameFieldState.error ? employerNamePath : undefined,
      verificationPhoneFieldState.error?.type !== "server" && verificationPhoneFieldState.error
        ? verificationPhonePath
        : undefined,
    ].filter((path): path is typeof employerNamePath => path !== undefined);
    if (staleClientPaths.length > 0) {
      void trigger(staleClientPaths);
    }
    // Intentionally keyed only on isEmployerRequired — re-checking on every
    // fieldState.error change would re-trigger validation on every blur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmployerRequired]);
  // Self-Employed (No W-2) / Cash Income require Occupation, mirroring the
  // live legacy validation in caps2's Step6ApplicantIncomeBackingBean (SVRS-55353).
  const isOccupationRequired = OCCUPATION_INCOME_TYPE_CODES.has(watchedIncomeType);
  const occupationPath = getFieldName(personPath, index, "occupation");
  const { fieldState: occupationFieldState } = useController({ control, name: occupationPath });
  useEffect(() => {
    if (occupationFieldState.error && occupationFieldState.error.type !== "server") {
      void trigger(occupationPath);
    }
    // Intentionally keyed only on isOccupationRequired — see isEmployerRequired above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOccupationRequired]);
  const [isOpen, setIsOpen] = useState(true);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showTaxes, setShowTaxes] = useState(false);
  // Survives the calculator panel's unmount/remount on toggle so the results
  // table reappears on reopen instead of requiring a re-Calculate.
  const [calculatorResult, setCalculatorResult] = useState<IncomeBreakdownResult | null>(null);
  const ordinal = INCOME_ORDINAL_LABELS[index] ?? `${index + 1}th`;
  const blockId = `${personPath}-income-${index}-content`;
  const calculatorPanelId = `${personPath}-income-${index}-calculator`;
  const taxesPanelId = `${personPath}-income-${index}-taxes`;
  const employerInfoPanelId = `${personPath}-income-${index}-employer-info`;
  const isLocked = index === 0;

  // Verification Outcome label (spec §8) — the latest Call Script outcome for
  // this employer slot, resolved from the applicant's saved history and this
  // slot's Employment id (`incomes[index].id`, hydrated from the BFF). History
  // is fetched here (not just from the modal) so the label reflects reality
  // on page load and after every save, per employer slot, without ever
  // caching/reusing another slot's value.
  const employmentIdPath = getFieldName(personPath, index, "id");
  const employmentId = useWatch({ control, name: employmentIdPath }) as string | undefined;
  const callScriptHistory = useCallScriptHistory(approvalId ?? "", applicantId ?? "", {
    enabled: Boolean(approvalId && applicantId),
  });
  const verificationOutcome = resolveLatestVerificationOutcome(callScriptHistory.data?.entries ?? [], employmentId);
  const verificationOutcomeColorClass =
    verificationOutcomeColor(verificationOutcome) === "green" ? "text-success-foreground" : "text-destructive";

  // ZIP -> City/State autofill: fills City and State when the user enters a
  // complete ZIP (the BFF returns a single city). `zipStatus` lets the ZIP
  // field surface a not-found / error state instead of silently no-op-ing.
  const { status: zipStatus, invalid: zipInvalid } = useZipAutofill({
    zipPath: getFieldName(personPath, index, "zip"),
    cityPath: getFieldName(personPath, index, "city"),
    statePath: getFieldName(personPath, index, "state"),
  });

  // grossMonthlyPay controller — overwritten reactively (below) and on Calculate
  // with the period gross monthly income.
  const grossMonthlyPayPath = getFieldName(personPath, index, "monthlyGrossPayAmount");
  const { field: grossMonthlyPayField } = useController({
    control,
    name: grossMonthlyPayPath,
  });
  const setGrossMonthlyPay = grossMonthlyPayField.onChange;
  // Derived, no visible input: mirrors Gross Monthly Pay on the hidden-calculator
  // path and takes the calculator's monthlyIncomePeriod on Calculate.
  const { field: monthlyIncomePerPayPeriodField } = useController({
    control,
    name: getFieldName(personPath, index, "monthlyIncomePerPayPeriod"),
  });
  const setMonthlyIncomePerPayPeriod = monthlyIncomePerPayPeriodField.onChange;

  // Pay Frequency is a single shared field (the calculator and the taxes row
  // bind to the same path and the same PAY_FREQUENCY_OPTIONS), so both stay in
  // sync and the factor reflects whichever control set it.
  const payFrequencyPath = getFieldName(personPath, index, "incomeFrequencyType");
  const { field: payFrequencyField } = useController({ control, name: payFrequencyPath });
  const setPayFrequency = payFrequencyField.onChange;
  // Setter for the Income-Type-driven Gross/Net force-set below; the reactive
  // read (watchedGrossNet) is declared further down, same split as payFrequency.
  const { field: payTypeField } = useController({ control, name: getFieldName(personPath, index, "payType") });
  const setPayType = payTypeField.onChange;
  const watchedPayPerPeriod = useWatch({
    control,
    name: getFieldName(personPath, index, "dscEnteredMonthlyIncomePerPayPeriod"),
  });
  const watchedPayFrequency = useWatch({ control, name: payFrequencyPath });
  const watchedPayDate = useWatch({ control, name: getFieldName(personPath, index, "payStubDate") });
  const watchedGrossPayYtd = useWatch({ control, name: getFieldName(personPath, index, "yearToDateIncomeAmount") });
  const watchedGrossNet = useWatch({ control, name: getFieldName(personPath, index, "payType") });

  // Pay-stub fields (grossPayYtd/payDate/payPeriodEndDate) — write access for the
  // outer Pay per Period guard below, which clears them for edits made while
  // the calculator is hidden. The calculator panel no longer has an
  // equivalent guard for edits made while it's mounted.
  const { field: grossPayYtdField } = useController({
    control,
    name: getFieldName(personPath, index, "yearToDateIncomeAmount"),
  });
  const { field: payDateField } = useController({ control, name: getFieldName(personPath, index, "payStubDate") });
  const { field: payPeriodEndDateField } = useController({
    control,
    name: getFieldName(personPath, index, "payPeriodEndDate"),
  });
  // Write access for the outer Pay per Period guard's cancel path, which
  // clears the field outright rather than reverting it.
  const { field: payPerPeriodField } = useController({
    control,
    name: getFieldName(personPath, index, "dscEnteredMonthlyIncomePerPayPeriod"),
  });

  // Default Pay Frequency to "Monthly" whenever it is empty (new sections and
  // hydrated incomes without a frequency alike). Runs through the shared field,
  // so the calculator and the taxes row both reflect it. This never recomputes
  // Gross Monthly Pay itself — recompute only ever runs from a direct user
  // interaction (see handleOuterPayPerPeriodBlur / handlePayFrequencyChange /
  // handleGrossNetChange below), so an auto-defaulted frequency can never
  // clobber a hydrated Gross Monthly Pay (e.g. monthlyGrossPayAmount).
  useEffect(() => {
    if (!watchedPayFrequency) {
      setPayFrequency("Monthly" satisfies PayFrequency);
    }
  }, [watchedPayFrequency, setPayFrequency]);

  // Overwrite Gross Monthly Pay with (Pay per Period × net-multiplier) × frequency factor.
  // No-ops unless Pay per Period is a positive number and a frequency is selected,
  // so a manually entered Gross Monthly Pay is otherwise left alone.
  // When grossNet is "Net", gross up by NET_PAY_FACTOR (1.21) before applying the
  // frequency factor, matching the calculator's income-basis treatment (OR-22309 Phase 3a).
  // Only ever called from a direct user-interaction handler (blur / select-change) —
  // never from a useWatch-driven effect — so mount/hydration can never trigger it.
  function recomputeGrossMonthlyPay(perPeriodValue: unknown, frequencyValue: unknown, grossNetValue = watchedGrossNet) {
    const perPeriod = Number(perPeriodValue);
    if (!frequencyValue || !perPeriod || perPeriod <= 0) {
      return;
    }
    const factor = PAY_FREQUENCY_FACTOR[frequencyValue as PayFrequency];
    if (!factor) {
      return;
    }
    const netMultiplier = grossNetValue === "Net" ? NET_PAY_FACTOR : 1;
    const monthlyPay = roundToDecimals(perPeriod * netMultiplier * factor, 2);
    setGrossMonthlyPay(monthlyPay);
    setMonthlyIncomePerPayPeriod(monthlyPay);
  }

  // Pay Frequency recomputes immediately on user selection. `next` is used
  // directly (not the stale watchedPayFrequency) since RHF hasn't committed
  // the new value yet at the point this fires.
  function handlePayFrequencyChange(next: string, apply: (value: string) => void) {
    apply(next);
    recomputeGrossMonthlyPay(watchedPayPerPeriod, next);
  }

  // Gross/Net recomputes immediately on user toggle, matching the live
  // Show-Taxes panel behaviour. `next` is used directly for the same reason
  // as handlePayFrequencyChange above.
  function handleGrossNetChange(next: string, apply: (value: string) => void) {
    apply(next);
    recomputeGrossMonthlyPay(watchedPayPerPeriod, watchedPayFrequency, next);
  }

  // Selecting Fixed Income (Net) forces Gross/Net to match and immediately
  // recomputes Gross Monthly Pay, mirroring caps2 Step6's
  // updateFirstIncomeType/getGrossOrNet + calculateGrossMonthlyPayFromPayPerPeriod
  // (Step6ApplicantIncomeModelBean). One-directional only, matching legacy:
  // switching away from Fixed Income does not reset Gross/Net back to Gross
  // (OR-23629).
  function handleIncomeTypeChange(next: string, apply: (value: string) => void) {
    apply(next);
    const defaultGrossNet = getDefaultGrossNet(next as IncomeTypeOption["value"]);
    if (defaultGrossNet === "Net") {
      setPayType(defaultGrossNet);
      recomputeGrossMonthlyPay(watchedPayPerPeriod, watchedPayFrequency, defaultGrossNet);
    }
  }

  // Outer Pay per Period data-loss guard (§8, COMMIT-stage) — for edits made
  // to this field while the calculator is hidden (the panel isn't mounted
  // then). The calculator panel itself no longer has an equivalent guard.
  //
  // Fires on blur (not on every keystroke): the field applies each keystroke
  // straight to RHF like a normal input, and outerEditStartReference records
  // the value the field held when it was last focused. On blur, that's
  // compared against the current value to tell a real edit apart from an
  // incidental blur (e.g. tabbing through without typing).
  const [outerConfirmOpen, setOuterConfirmOpen] = useState(false);
  const outerEditStartReference = useRef<number | "">("");
  // Set true (synchronously, inside confirmAction.onClick) right before it
  // requests the dialog close — lets onOpenChange tell a confirm apart from
  // a genuine Cancel / Esc / overlay dismiss.
  const outerConfirmedReference = useRef(false);

  function outerHasPayStubData(): boolean {
    return Boolean(watchedGrossPayYtd || watchedPayDate || payPeriodEndDateField.value || calculatorResult !== null);
  }

  function currentPayPerPeriod(): number | "" {
    return typeof watchedPayPerPeriod === "number" ? watchedPayPerPeriod : "";
  }

  function handleOuterPayPerPeriodFocus() {
    outerEditStartReference.current = currentPayPerPeriod();
  }

  // Recomputes on blur (not on every keystroke) when there's no pay-stub data
  // to lose, or nothing actually changed since focus. Otherwise defers to the
  // confirm dialog — Continue recomputes with the now-applied value, Cancel
  // clears the field and leaves Gross Monthly Pay untouched.
  function handleOuterPayPerPeriodBlur() {
    const current = currentPayPerPeriod();
    if (current === outerEditStartReference.current) {
      return;
    }
    if (outerHasPayStubData()) {
      setOuterConfirmOpen(true);
      return;
    }
    recomputeGrossMonthlyPay(current, watchedPayFrequency);
  }

  // Show Taxes computes live from the raw Pay per Period (period) and Gross Pay
  // YTD (YTD), matching CAPS2 parity. payDate (when set) selects the SS rate year.
  // Taxes always use raw values — the Net-pay ×1.21 gross-up is quick-path only.
  // Frozen to the pre-edit Pay per Period while the outer confirm dialog is
  // pending — otherwise Taxes would show a breakdown for a per-period/YTD
  // combination that can never persist (Confirm clears YTD, Cancel clears
  // Pay per Period).
  const perPeriodForTaxes = Number(outerConfirmOpen ? outerEditStartReference.current : watchedPayPerPeriod) || 0;
  const taxesBreakdown =
    perPeriodForTaxes > 0
      ? computeTaxes(
          perPeriodForTaxes,
          Number(watchedGrossPayYtd) || 0,
          typeof watchedPayDate === "string" ? watchedPayDate : undefined
        )
      : null;

  function handleToggle() {
    setIsOpen((previous) => !previous);
  }

  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  function handleRemove(event: React.MouseEvent) {
    event.stopPropagation();
    setConfirmRemoveOpen(true);
  }

  function handleToggleCalculator() {
    setShowCalculator((previous) => {
      const next = !previous;
      // Opening the calculator hides the Pay/Frequency/Gross-Net + Show Taxes
      // row, so collapse the taxes panel too (its toggle is no longer visible).
      if (next) {
        setShowTaxes(false);
      }
      return next;
    });
  }

  function handleToggleTaxes() {
    setShowTaxes((previous) => !previous);
  }

  // Called by IncomeCalculatorPanel when Calculate succeeds.
  // Overwrites Gross Monthly Pay with the YTD-basis gross monthly income
  // (factor * (grossPayYTD / payPeriods) = monthlyIncomeYtd), matching
  // CAPS2 Step6 §3 `calculateIncome`. Period-basis only feeds the ratio popup.
  function handleCalculated(result: IncomeBreakdownResult) {
    grossMonthlyPayField.onChange(result.monthlyIncomeYtd);
    // A zeroed breakdown (payPeriods <= 0) must not overwrite a real value.
    if (result.monthlyIncomePeriod > 0) {
      setMonthlyIncomePerPayPeriod(result.monthlyIncomePeriod);
    }
    setCalculatorResult(result);
  }

  // Render a single income field by name, looked up from the declarative config.
  function renderField(
    name: keyof IncomeInput,
    options?: {
      onFocus?: () => void;
      onBlur?: () => void;
      invalid?: boolean;
      optionsOverride?: ReadonlyArray<{ value: string; label: string }>;
      disabled?: boolean;
      requiredOverride?: boolean;
      onChangeOverride?: (next: number | "", apply: (value: number | "") => void) => void;
      onValueChangeOverride?: (next: string, apply: (value: string) => void) => void;
    }
  ) {
    // "differentWorkLocation" (the sole checkbox field) is never looked up
    // through renderField — it's rendered by its own hardcoded block below —
    // so this cast is safe.
    const config = INCOME_FIELDS.find((f) => f.name === name) as RenderableFieldConfig | undefined;
    if (!config) {
      return null;
    }
    return (
      <FieldRenderer
        config={config}
        control={control}
        personPath={personPath}
        index={index}
        onFocus={options?.onFocus}
        onBlur={options?.onBlur}
        invalid={options?.invalid}
        optionsOverride={options?.optionsOverride}
        disabled={options?.disabled}
        requiredOverride={options?.requiredOverride}
        onChangeOverride={options?.onChangeOverride}
        onValueChangeOverride={options?.onValueChangeOverride}
      />
    );
  }

  return (
    <div>
      <SubsectionHeader
        title={t("review.income.blockTitle", { position: ordinal })}
        isOpen={isOpen}
        onToggle={handleToggle}
        contentId={blockId}
        hasError={hasError}
        className={index > 0 ? "py-1" : undefined}
        beforeIcons={
          <span className="min-w-0 text-xs font-normal">
            {t("review.income.verificationOutcome")}:{" "}
            <span className={cn("font-bold", verificationOutcomeColorClass)}>{verificationOutcome}</span>
          </span>
        }
        trailing={
          !isLocked && onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={handleRemove}
              aria-label={t("review.income.removeIncome", { position: ordinal })}
              className="text-destructive hover:text-destructive shrink-0"
            >
              <Trash2Icon className="size-4" aria-hidden="true" />
            </Button>
          ) : undefined
        }
      />

      {/* Block content */}
      {isOpen && (
        <div id={blockId} className="flex flex-col gap-2 px-3 pb-4 pt-3">
          {/* Income Type — own row; options gated by the applicant's SSN/ITIN status */}
          <div className="@sm:grid-cols-2 grid grid-cols-1 items-start gap-x-3 gap-y-2">
            {renderField("employmentType", {
              optionsOverride: incomeTypeOptions,
              onValueChangeOverride: handleIncomeTypeChange,
            })}
          </div>

          {/* Employer Name | Occupation */}
          <div className="@sm:grid-cols-2 grid grid-cols-1 items-start gap-x-3 gap-y-2">
            {renderField("employerName", { requiredOverride: isEmployerRequired })}
            {renderField("occupation", { requiredOverride: isOccupationRequired })}
          </div>

          {/* Address — Street No. (2) | Street Name (8) | Ste # (2) on a 12-col grid */}
          <fieldset className="flex flex-col gap-1">
            <legend className="text-foreground text-sm font-medium">{t("review.income.address")}</legend>
            <div className="@sm:grid-cols-12 grid grid-cols-1 items-start gap-x-3 gap-y-2">
              <div className="@sm:col-span-2">{renderField("streetNumber")}</div>
              <div className="@sm:col-span-8">{renderField("streetName")}</div>
              <div className="@sm:col-span-2">{renderField("apartmentNumber")}</div>
            </div>
          </fieldset>

          {/* Zip | City | State — stacked when narrow; Zip+City share a row with
              State below at @sm; full 3/5/4 single row at @lg (sized off this
              column's own width, not the viewport — see Signers.tsx's SignerGridCell @container) */}
          <div className="@sm:grid-cols-12 grid grid-cols-1 items-start gap-x-3 gap-y-2">
            <div className="@sm:col-span-6 @lg:col-span-3">
              {renderField("zip", { invalid: zipInvalid })}
              <ZipLookupMessage status={zipStatus} />
            </div>
            <div className="@sm:col-span-6 @lg:col-span-5">{renderField("city")}</div>
            <div className="@sm:col-span-12 @lg:col-span-4">{renderField("state")}</div>
          </div>

          {/* Employment Verification Phone + Extn (grouped so neither shifts on error) */}
          <PhoneExtensionField
            control={control}
            personPath={personPath}
            index={index}
            required={isEmployerRequired}
            approvalId={approvalId}
            applicantId={applicantId}
          />

          {/* Different work location checkbox — full width */}
          <DifferentWorkLocationField control={control} personPath={personPath} index={index} />

          {/* Gross Monthly Pay | Hire Date | Show Calculator — side by side.
              Hire Date renders here only while the calculator is closed; when the
              calculator opens it moves into the panel as the first field (same RHF
              field, so the value persists across the toggle). */}
          <div className="@sm:flex-row @sm:flex-wrap @sm:items-start flex flex-col gap-2">
            {/* Gross Monthly Pay is always derived — recomputeGrossMonthlyPay
                (Pay per Period blur / frequency / Gross-Net change) and the
                Calculator's Calculate button own it, so the control is never
                directly editable. */}
            {renderField("monthlyGrossPayAmount", { disabled: true, requiredOverride: isGrossPayRequired })}
            {/* When the calculator is open Hire Date moves into the panel; reserve
                its slot here (@sm+) so the Show/Hide Calculator button stays put. */}
            {showCalculator ? <div aria-hidden="true" className="@sm:block hidden w-32" /> : renderField("hireDate")}
            {/* Show Calculator — an invisible label-height spacer (@sm+) keeps the
                button level with the inputs and fixed there when a sibling field
                shows an error (the error grows below the inputs, not here). */}
            <div className="flex flex-col gap-2">
              <span aria-hidden="true" className="@sm:block hidden h-3">
                {" "}
              </span>
              {/* Show / Hide Calculator toggle */}
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="cursor-pointer px-0 underline"
                  aria-expanded={showCalculator}
                  aria-controls={calculatorPanelId}
                  onClick={handleToggleCalculator}
                >
                  {showCalculator ? t("review.income.hideCalculator") : t("review.income.showCalculator")}
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={t("review.income.calculatorHelp.ariaLabel")}
                      className="text-muted-foreground hover:text-foreground cursor-pointer"
                    >
                      <HelpCircleIcon className="size-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-2xl text-pretty p-4 text-left text-xs">
                    <p className="mb-2">
                      <strong className="text-sky-300 underline">
                        {t("review.income.calculatorHelp.directionsHeading")}
                      </strong>
                    </p>
                    <ol className="list-decimal space-y-1 pl-4">
                      <li>{t("review.income.calculatorHelp.step1")}</li>
                      <li>{t("review.income.calculatorHelp.step2")}</li>
                      <li>{t("review.income.calculatorHelp.step3")}</li>
                      <li>{t("review.income.calculatorHelp.step4")}</li>
                      <li>{t("review.income.calculatorHelp.step5")}</li>
                    </ol>
                    <p className="mb-1 mt-3">{t("review.income.calculatorHelp.noPayStubHeading")}</p>
                    <ol className="list-decimal space-y-1 pl-4">
                      <li>{t("review.income.calculatorHelp.noPayStubStep1")}</li>
                      <li>{t("review.income.calculatorHelp.noPayStubStep2")}</li>
                      <li>{t("review.income.calculatorHelp.noPayStubStep3")}</li>
                    </ol>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Income Calculator panel — inline collapsible */}
          {showCalculator && (
            <IncomeCalculatorPanel
              control={control}
              personPath={personPath}
              index={index}
              panelId={calculatorPanelId}
              onCalculated={handleCalculated}
              initialResult={calculatorResult}
            />
          )}

          {/* Pay per Period | Pay Frequency | Gross/Net | Show Taxes — one row.
              Hidden while the calculator is open (it owns Pay Frequency / per-period
              already); field values persist in RHF and reappear when it closes. */}
          {!showCalculator && (
            <div className="@sm:flex-row @sm:flex-wrap @sm:items-start flex flex-col gap-2">
              {renderField("dscEnteredMonthlyIncomePerPayPeriod", {
                onFocus: handleOuterPayPerPeriodFocus,
                onBlur: handleOuterPayPerPeriodBlur,
              })}
              {renderField("incomeFrequencyType", { onValueChangeOverride: handlePayFrequencyChange })}
              {renderField("payType", { onValueChangeOverride: handleGrossNetChange })}
              {/* Show / Hide Taxes toggle — spacer keeps button level with inputs */}
              <div className="flex flex-col gap-2">
                <span aria-hidden="true" className="@sm:block hidden h-3" />
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="cursor-pointer px-0 underline"
                  aria-expanded={showTaxes}
                  aria-controls={taxesPanelId}
                  onClick={handleToggleTaxes}
                >
                  {showTaxes ? t("review.income.hideTaxes") : t("review.income.showTaxes")}
                </Button>
              </div>
            </div>
          )}

          {/* Outer Pay per Period — data-loss confirm dialog (§8, COMMIT-stage),
              for edits made here while the calculator is hidden. The
              calculator panel itself no longer has an equivalent dialog. */}
          <ConfirmDialog
            open={outerConfirmOpen}
            onOpenChange={(open) => {
              if (open) {
                return;
              }
              // Radix's AlertDialog.Action also requests a close after running
              // confirmAction.onClick below, so this fires on confirm too —
              // outerConfirmedReference distinguishes that from a genuine
              // Cancel / Esc / overlay dismiss.
              if (outerConfirmedReference.current) {
                outerConfirmedReference.current = false;
                setOuterConfirmOpen(false);
                return;
              }
              // Cancel (or Esc / overlay dismiss): the already-typed edit is
              // discarded by clearing the field outright, not reverted to
              // its pre-edit value. Gross Monthly Pay is left untouched.
              payPerPeriodField.onChange("");
              outerEditStartReference.current = "";
              setOuterConfirmOpen(false);
            }}
            title={t("review.income.calculator.confirmDataLoss.title")}
            description={t("review.income.calculator.warnings.editWillClearPayStubData", { ord: ordinal })}
            cancelAction={{ label: t("review.income.calculator.confirmDataLoss.cancel") }}
            confirmAction={{
              label: t("review.income.calculator.confirmDataLoss.confirm"),
              onClick: () => {
                outerConfirmedReference.current = true;
                grossPayYtdField.onChange("");
                payDateField.onChange("");
                payPeriodEndDateField.onChange("");
                setCalculatorResult(null);
                const current = currentPayPerPeriod();
                outerEditStartReference.current = current;
                recomputeGrossMonthlyPay(current, watchedPayFrequency);
                setOuterConfirmOpen(false);
              },
            }}
          />

          {/* Taxes panel — inline collapsible; computed live from Gross Monthly Pay */}
          {showTaxes && <IncomeTaxesPanel panelId={taxesPanelId} taxes={taxesBreakdown} />}

          {/* Pull Purchased Employer Info — eligibility-gated button + results table */}
          <PullPurchasedEmployerInfo
            approvalId={approvalId}
            applicantId={applicantId}
            panelId={employerInfoPanelId}
            employmentType={watchedIncomeType}
            onRecalcDeferred={onWorknumberRecalcDeferred}
            // Only the applicant's W2 income block's eligibility check drives
            // the applicant-level "not found" warning above the Income
            // section — a non-W2 block never fetches eligibility (see
            // PullPurchasedEmployerInfo's `hasIds`), so gating on income type
            // rather than array index ensures the warning/disabled-button
            // signal comes from whichever block the feature actually applies
            // to, regardless of income ordering (OR/SVRS-56136).
            onEligibilityLastPullNotFound={
              watchedIncomeType === W2_INCOME_TYPE_CODE ? onEmployerInfoLastPullNotFound : undefined
            }
            employerInfoNotFound={employerInfoNotFound}
          />

          {/* Additional Information — full width */}
          {renderField("notes")}
        </div>
      )}

      {/* Remove-income confirm dialog — rendered outside the collapsible content
          so it's available even when the block is collapsed (the trash icon
          lives in the header, which is always visible). */}
      <ConfirmDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title={t("review.income.confirmRemove.title")}
        description={t("review.income.confirmRemove.message", { position: ordinal })}
        cancelAction={{ label: t("review.income.confirmRemove.cancel") }}
        confirmAction={{ label: t("review.income.confirmRemove.ok"), onClick: () => onRemove?.() }}
      />
    </div>
  );
}
